import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Copy, ArrowRight, Package, Mail, Phone, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);

  // Get session_id from URL if coming from Stripe
  const sessionId = searchParams.get('session_id');
  const orderIdFromUrl = searchParams.get('order_id');
  const finalOrderId = orderId || orderIdFromUrl;

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        // If coming from Stripe, verify payment status first
        if (sessionId) {
          try {
            const statusRes = await axios.get(`${API}/checkout/stripe/status/${sessionId}`);
            if (statusRes.data.payment_status === 'paid') {
              setPaymentVerified(true);
              toast.success('Pagamento confirmado!');
            }
          } catch (e) {
            console.error('Error verifying payment:', e);
          }
        }

        const response = await axios.get(`${API}/orders/${finalOrderId}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };
    if (finalOrderId) {
      fetchOrder();
    } else {
      setLoading(false);
    }
  }, [finalOrderId, sessionId]);

  const copyOrderId = () => {
    navigator.clipboard.writeText(order.id);
    toast.success('Número do pedido copiado!');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4">Pedido não encontrado</p>
          <Link to="/shop">
            <Button className="btn-primary">Voltar à Loja</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="order-confirmation-page">
      <div className="container-luxury py-12">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 mx-auto mb-6 border-2 border-green-500 rounded-full flex items-center justify-center animate-fade-in">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-serif text-4xl mb-4">Pedido Confirmado!</h1>
            <p className="text-muted-foreground">
              Obrigado pela sua compra. Você receberá um email com os detalhes do pedido.
            </p>
          </div>

          {/* Order Number */}
          <div className="bg-card border border-white/10 p-6 mb-8 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Número do Pedido</p>
              <p className="text-2xl font-bold text-primary" data-testid="order-id">
                #{order.id}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={copyOrderId}
              className="btn-outline"
              data-testid="copy-order-id-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar
            </Button>
          </div>

          {/* Payment Instructions for Zelle */}
          {order.payment_method === 'zelle' && order.zelle_info && (
            <div className="bg-primary/10 border border-primary/30 p-8 mb-8">
              <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
                <span className="text-primary">⚡</span>
                Instruções de Pagamento via Zelle
              </h2>
              <div className="space-y-4 text-sm">
                <p>Para completar seu pedido, envie o pagamento via Zelle usando as informações abaixo:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-background/50 p-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-muted-foreground text-xs">Email</p>
                      <p>{order.zelle_info.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-muted-foreground text-xs">Telefone</p>
                      <p>{order.zelle_info.phone}</p>
                    </div>
                  </div>
                </div>
                <p className="text-primary font-medium">
                  ⓘ Inclua o número do pedido #{order.id} no memo do pagamento
                </p>
              </div>
            </div>
          )}

          {/* Cash Payment Info */}
          {order.payment_method === 'cash' && (
            <div className="bg-card border border-white/10 p-8 mb-8">
              <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
                <span className="text-primary">💵</span>
                Pagamento em Dinheiro
              </h2>
              <p className="text-muted-foreground">
                O pagamento será coletado no momento da entrega. 
                Por favor, tenha o valor exato de <strong className="text-primary">${order.total.toFixed(2)}</strong> em mãos.
              </p>
            </div>
          )}

          {/* Order Details */}
          <div className="bg-card border border-white/10 p-8 mb-8">
            <h2 className="font-serif text-xl mb-6">Detalhes do Pedido</h2>
            
            {/* Items */}
            <div className="space-y-4 mb-6">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center py-3 border-b border-white/5 last:border-0">
                  <div>
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.size} | {item.color} | x{item.quantity}
                    </p>
                  </div>
                  <p className="text-primary">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-primary">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-card border border-white/10 p-8 mb-8">
            <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Endereço de Entrega
            </h2>
            <div className="text-muted-foreground">
              <p>{order.shipping_address.street}</p>
              <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
              <p>{order.shipping_address.country}</p>
            </div>
          </div>

          {/* Status */}
          <div className="bg-card border border-white/10 p-8 mb-8">
            <h2 className="font-serif text-xl mb-4">Status</h2>
            <div className="flex gap-4">
              <span className={`status-badge status-${order.order_status}`}>
                {order.order_status}
              </span>
              <span className={`status-badge status-${order.payment_status}`}>
                Pagamento: {order.payment_status}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/profile">
              <Button variant="outline" className="btn-outline w-full sm:w-auto">
                Ver Meus Pedidos
              </Button>
            </Link>
            <Link to="/shop">
              <Button className="btn-primary w-full sm:w-auto" data-testid="continue-shopping-btn">
                Continuar Comprando
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
